import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { apiEvents, SIGNED_OUT, ACCESS_CHECK } from './lib/api';
import { keys, queryClient } from './lib/queries';
import type { TenantBoot } from './lib/tenant';
import { TenantProvider } from './lib/tenant-context';
import { clearPrivateStorage, purgeLegacyCaches, PRIVATE_PREFIX } from './lib/private-state';
import { router } from './router';

export function App({ tenant }: { tenant: TenantBoot }) {
  // Account-session 401s clear private state; review-gate expiry preserves it behind the access guard.
  useEffect(() => {
    void purgeLegacyCaches();
    const onSignedOut = () => {
      clearPrivateStorage();
      void purgeLegacyCaches();
      // Keep the observed session query: clearing it detaches the mounted route guards.
      void queryClient.cancelQueries();
      queryClient.removeQueries({
        predicate: (query) => !['session', 'tenant'].includes(String(query.queryKey[0])),
      });
      queryClient.setQueryData(keys.session, { authenticated: false, user: null, membership: null });
    };
    apiEvents.addEventListener(SIGNED_OUT, onSignedOut);
    const check = () => {
      void queryClient.invalidateQueries({ queryKey: keys.session });
    };
    const otherTab = (event: StorageEvent) => {
      if (event.key === `${PRIVATE_PREFIX}owner` || event.key === null) {
        // A fresh document also discards component state and callbacks from the previous account.
        window.location.reload();
      }
    };
    apiEvents.addEventListener(ACCESS_CHECK, check);
    window.addEventListener('storage', otherTab);
    return () => {
      apiEvents.removeEventListener(SIGNED_OUT, onSignedOut);
      apiEvents.removeEventListener(ACCESS_CHECK, check);
      window.removeEventListener('storage', otherTab);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider tenant={tenant}>
        <RouterProvider router={router} />
      </TenantProvider>
    </QueryClientProvider>
  );
}
